import { Controller, useForm } from 'react-hook-form';
import { Skeleton } from '@mantine/core';

import { FilesSearchWidget } from 'types/dashboard_widget';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useCameras } from 'cameras/hooks/useCameras';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { useGateways } from 'gateways/hooks/useGateways';
import { useModal } from 'hooks/useModal';
import { useStreams } from 'streams/hooks/useStreams';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';

import { Accordion } from 'components/Accordion/Accordion';
import { CameraSelect } from 'cameras/components/CameraSelect/CameraSelect';
import { Field } from 'components/Field/Field';
import { FilesSearchConfidenceSlider } from 'files/components/FilesSearchConfidenceSlider/FilesSearchConfidenceSlider';
import { FilesSearchEntitiesInput } from 'files/components/FilesSearchEntitiesInput/FilesSearchEntitiesInput';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { Input } from 'components/Input/Input';
import { OptionalTooltip } from 'components/Tooltip/OptionalTooltip';
import { Radio, RadioOption } from 'components/Radio/Radio';
import { StreamSelect } from 'streams/components/StreamSelect/StreamSelect';

import { WidgetFormHeader } from './Header';
import { WidgetFormFooter } from './Footer';

type FilesSearchFormFieldValues = FilesSearchWidget['options'] & {
  name: string;
};

type FilesSearchForm = { widget?: FilesSearchWidget; dashboardId: string };

export function FilesSearchForm({ widget, dashboardId }: FilesSearchForm) {
  const { close } = useModal();

  const { data: dashboard } = useDashboard(dashboardId);

  const options: FilesSearchWidget['options'] = widget
    ? widget.options
    : {
        query: '',
        search_entities: ['events', 'objects', 'texts', 'vectors'],
        confidence: 0.5,
        sort_by: 'relevance',
        camera_ids: [],
        stream_ids: [],
        gateway_ids: [],
      };

  const {
    control,
    formState: { dirtyFields, errors },
    handleSubmit,
    register,
    watch,
  } = useForm<FilesSearchFormFieldValues>({
    defaultValues: widget ? { name: widget.name, ...options } : options,
  });

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  const { mutate: updateDashboard, isLoading: isUpdating } = useUpdateDashboard(
    {
      onSuccess: () => close(),
    }
  );

  const hasDefaultCameras = options.camera_ids.length > 0;
  const hasDefaultStreams = options.stream_ids.length > 0;
  const hasDefaultGateways = options.gateway_ids.length > 0;

  const { data: cameras, isInitialLoading: isCamerasLoading } = useCameras({
    params: {
      camera_ids: options.camera_ids,
      limit: options.camera_ids.length,
    },
    enabled: hasDefaultCameras,
  });

  const { data: streams, isInitialLoading: isStreamsLoading } = useStreams({
    params: {
      stream_ids: options.stream_ids,
      limit: options.stream_ids.length,
    },
    enabled: hasDefaultStreams,
  });

  const { data: gateways, isInitialLoading: isGatewaysLoading } = useGateways({
    params: {
      gateway_ids: options.gateway_ids,
      limit: options.gateway_ids.length,
    },
    enabled: hasDefaultGateways,
  });

  function onSubmit({ name, ...options }: FilesSearchFormFieldValues) {
    if (!dashboard) {
      return;
    }

    const id = widget ? widget.id : crypto.randomUUID();
    const layout = widget
      ? widget.layout
      : getNewWidgetLayout(dashboard.configuration.widgets, 4, 5);

    const updatedDashboard = upsertWidget(
      dashboard,
      new FilesSearchWidget(id, name, options, layout)
    );

    updateDashboard({
      id: updatedDashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  const searchEntities = watch('search_entities');
  const isConfidenceSliderDisabled = !(
    searchEntities.length && searchEntities.includes('vectors')
  );

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <WidgetFormHeader type="files-search" />
      <Field label="Widget name" error={errors.name} required>
        <Input
          {...register('name', {
            required: 'Please choose a name for this widget.',
          })}
        />
      </Field>

      <Field label="Search text" error={errors.query} required>
        <Input
          {...register('query', {
            required: 'Please add a search text.',
          })}
          placeholder="ex. person"
        />
      </Field>

      <Field label="Confidence">
        <Controller
          name="confidence"
          control={control}
          render={({ field: { onChange, value } }) => (
            <OptionalTooltip
              showTooltip={isConfidenceSliderDisabled}
              content="Enable scenes search to set confidence"
            >
              <FilesSearchConfidenceSlider
                value={value}
                onChangeEnd={onChange}
                disabled={isConfidenceSliderDisabled}
              />
            </OptionalTooltip>
          )}
        />
      </Field>

      <Field label="Search in">
        <Controller
          name="search_entities"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FilesSearchEntitiesInput
              id="files-search-entities-input-widget"
              value={value}
              onChange={onChange}
            />
          )}
        />
      </Field>

      <Field label="Sort by">
        <Controller
          name="sort_by"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Radio id="search_sort" value={value} onChange={onChange}>
              <RadioOption value="relevance">Relevance</RadioOption>
              <RadioOption value="created_at">Date</RadioOption>
            </Radio>
          )}
        />
      </Field>

      <Accordion
        variant="flat"
        label="Additional filters"
        defaultOpen={
          options.camera_ids.length > 0 ||
          options.stream_ids.length > 0 ||
          options.gateway_ids.length > 0
        }
      >
        <Field label="Cameras">
          <Controller
            name="camera_ids"
            control={control}
            render={({ field: { onChange } }) => {
              if (!hasDefaultCameras) {
                return (
                  <CameraSelect
                    size="small"
                    menuPlacement="top"
                    onChange={(cameras) =>
                      onChange(cameras.map(({ id }) => id))
                    }
                    isMulti
                  />
                );
              }

              if (isCamerasLoading || !cameras) {
                return <Skeleton height="2rem" width="100%" />;
              }

              return (
                <CameraSelect
                  size="small"
                  menuPlacement="top"
                  defaultValue={cameras.data}
                  onChange={(cameras) => onChange(cameras.map(({ id }) => id))}
                  isMulti
                />
              );
            }}
          />
        </Field>

        <Field label="Streams">
          <Controller
            name="stream_ids"
            control={control}
            render={({ field: { onChange } }) => {
              if (!hasDefaultStreams) {
                return (
                  <StreamSelect
                    size="small"
                    menuPlacement="top"
                    onChange={(streams) =>
                      onChange(streams.map(({ id }) => id))
                    }
                    queryFilters={{
                      sources: ['camera_stream', 'uri_stream'],
                      stream_types: ['file', 'rtsp'],
                    }}
                    isMulti
                  />
                );
              }

              if (isStreamsLoading || !streams) {
                return <Skeleton height="2rem" width="100%" />;
              }

              return (
                <StreamSelect
                  size="small"
                  defaultValue={streams.data}
                  menuPlacement="top"
                  onChange={(streams) => onChange(streams.map(({ id }) => id))}
                  queryFilters={{
                    sources: ['camera_stream', 'uri_stream'],
                    stream_types: ['file', 'rtsp'],
                  }}
                  isMulti
                />
              );
            }}
          />
        </Field>

        <Field label="Gateways">
          <Controller
            name="gateway_ids"
            control={control}
            render={({ field: { onChange } }) => {
              if (!hasDefaultGateways) {
                return (
                  <GatewaySelect
                    size="small"
                    menuPlacement="top"
                    onChange={(gateways) =>
                      onChange(gateways.map(({ id }) => id))
                    }
                    isMulti
                  />
                );
              }

              if (isGatewaysLoading || !gateways) {
                return <Skeleton height="2rem" width="100%" />;
              }

              return (
                <GatewaySelect
                  size="small"
                  menuPlacement="top"
                  defaultValue={gateways.data}
                  onChange={(gateways) =>
                    onChange(gateways.map(({ id }) => id))
                  }
                  isMulti
                />
              );
            }}
          />
        </Field>
      </Accordion>

      <WidgetFormFooter isDirty={isDirty} isLoading={isUpdating} />
    </form>
  );
}
